import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import InlineEmailOtpVerification from './common/InlineEmailOtpVerification';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    programs: [
      { name: 'All Programs', href: '/programs' },
      { name: '3-Day Immersive', href: '/programs/3-day-immersive' },
      { name: '4-Saturday Flexible', href: '/programs/4-saturday-flexible' },
      { name: 'ODC 3-Hour Workshops', href: '/programs/odc-workshops' },
    ],
    company: [
      { name: 'About Us', href: '/about' },
      { name: 'Our Team', href: '/about#team' },
      { name: 'Why Choose Us', href: '/why-choose-us' },
    ],
    resources: [
      { name: 'Schedule', href: '/schedule' },
      { name: 'FAQ', href: '/faq' },
      { name: 'Contact Us', href: '/contact' },
    ],
    legal: [
      { name: 'Privacy Policy', href: '/privacy' },
      { name: 'Terms & Conditions', href: '/terms' },

    ],
  };

  const socialLinks = [
    { name: 'facebook', link: 'https://www.facebook.com/share/r/18gE2WjkA9/', icon: '/assets/icons/social/facebook.svg' },
    { name: 'linkedin', link: 'https://www.linkedin.com/feed/update/urn:li:activity:7301163293774168064', icon: '/assets/icons/social/linkedin.svg' },
    { name: 'youtube', link: 'https://youtu.be/MhYGuLrWnZo', icon: '/assets/icons/social/youtube.svg' },
    { name: 'whatsapp', link: 'https://wa.me/918454944090', icon: '/assets/icons/social/whatsapp.svg' },
    {
      name: 'email',
      link: 'mailto:connect@list.academy',
      isSvg: true,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2-2v10a2 2 0 002 2z" />
        </svg>
      )
    },
  ];

  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');
  const [verifiedEmail, setVerifiedEmail] = useState('');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    if (verifiedEmail !== email.trim().toLowerCase()) {
      setStatus('error');
      setMessage('Verify your email with OTP before subscribing.');
      return;
    }

    setStatus('loading');
    try {
      const res = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();
      if (data.success) {
        setStatus('success');
        setMessage(data.message);
        setEmail('');
        setTimeout(() => setStatus('idle'), 5000);
      } else {
        setStatus('error');
        setMessage(data.message || 'Something went wrong');
        setTimeout(() => setStatus('idle'), 5000);
      }
    } catch (err) {
      setStatus('error');
      setMessage('Failed to subscribe. Please try again.');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  return (
    <footer className="bg-brand-dark text-white relative overflow-hidden mt-auto">
      {/* Background Decorations */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-brand-primary/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-64 h-64 bg-brand-primary/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 relative z-10">
        {/* Top Section - Modern Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 mb-12">
          {/* Brand Section */}
          <div className="lg:col-span-4 space-y-8">
            <div className="space-y-6">
              <p className="text-gray-300 text-sm leading-relaxed max-w-sm">
                Transformative leadership development built on the L.I.S.T framework: Learning, Inspiring, Strategizing, and Transforming.
              </p>

              {/* Social Links */}
              <div className="flex flex-wrap gap-4">
                {socialLinks.map((social) => (
                  <Link
                    key={social.name}
                    href={social.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-brand-primary hover:border-brand-primary hover:-translate-y-1 transition-all duration-300 group shadow-lg"
                  >
                    {social.isSvg ? (
                      <span className="text-white group-hover:scale-110 transition-transform">
                        {social.icon}
                      </span>
                    ) : (
                      <Image
                        src={social.icon}
                        width={18}
                        height={18}
                        alt={social.name}
                        className="brightness-0 invert group-hover:scale-110 transition-transform"
                      />
                    )}
                  </Link>
                ))}
              </div>
            </div>

            {/* Subscription Form Moved Here */}
            <div className="space-y-4">
              <h4 className="text-sm font-bold text-white uppercase tracking-widest opacity-60">Newsletter</h4>
              {status === 'success' ? (
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg px-4 py-2.5 max-w-[320px] flex items-center gap-2">
                  <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-sm font-bold text-green-500">Subscribed!</span>
                </div>
              ) : (
                <>
                  <form
                    onSubmit={handleSubscribe}
                    className="flex rounded-lg overflow-hidden bg-white/5 border border-white/10 focus-within:border-brand-primary/50 transition-all max-w-[320px] w-full group"
                  >
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Subscribe to insights"
                      className="bg-transparent text-sm px-4 py-2.5 outline-none w-full placeholder:text-gray-500"
                      required
                      disabled={status === 'loading'}
                    />
                    <button
                      type="submit"
                      disabled={status === 'loading'}
                      className="bg-brand-primary px-4 py-2.5 text-sm font-bold text-white hover:bg-brand-dark transition-colors disabled:opacity-50"
                    >
                      {status === 'loading' ? '...' : 'Join'}
                    </button>
                  </form>
                  <InlineEmailOtpVerification
                    email={email}
                    verifiedEmail={verifiedEmail}
                    onVerifiedEmailChange={setVerifiedEmail}
                    context="newsletter"
                    mode="manual"
                  />
                  {status === 'error' && (
                    <p className="text-[10px] text-red-500 font-medium ml-1">{message}</p>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Links Sections - Modern 5-Column Layout (Programs is prioritized) */}
          <div className="lg:col-span-8">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-12">
              {/* Programs (Spans 2 columns, larger text) */}
              <div className="col-span-2 space-y-5">
                <h3 className="text-xl font-bold text-white tracking-tight flex items-center gap-2">
                  <span className="w-1.5 h-6 bg-brand-primary rounded-full"></span>
                  Programs
                </h3>
                <ul className="space-y-3">
                  {footerLinks.programs.map((link) => (
                    <li key={link.name}>
                      <Link
                        href={link.href}
                        className="text-gray-300 hover:text-brand-primary transition-colors text-base font-medium flex items-center group"
                      >
                        <span className="w-0 group-hover:w-3 h-px bg-brand-primary mr-0 group-hover:mr-2 transition-all duration-300"></span>
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Company */}
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-white uppercase tracking-wider opacity-80">
                  Company
                </h3>
                <ul className="space-y-2">
                  {footerLinks.company.map((link) => (
                    <li key={link.name}>
                      <Link
                        href={link.href}
                        className="text-gray-300 hover:text-brand-primary transition-colors text-sm flex items-center group"
                      >
                        <span className="w-0 group-hover:w-2 h-px bg-brand-primary mr-0 group-hover:mr-2 transition-all duration-300"></span>
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Resources */}
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-white uppercase tracking-wider opacity-80">
                  Resources
                </h3>
                <ul className="space-y-2">
                  {footerLinks.resources.map((link) => (
                    <li key={link.name}>
                      <Link
                        href={link.href}
                        className="text-gray-300 hover:text-brand-primary transition-colors text-sm flex items-center group"
                      >
                        <span className="w-0 group-hover:w-2 h-px bg-brand-primary mr-0 group-hover:mr-2 transition-all duration-300"></span>
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Legal */}
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-white uppercase tracking-wider opacity-80">
                  Legal
                </h3>
                <ul className="space-y-2">
                  {footerLinks.legal.map((link) => (
                    <li key={link.name}>
                      <Link
                        href={link.href}
                        className="text-gray-300 hover:text-brand-primary transition-colors text-sm flex items-center group"
                      >
                        <span className="w-0 group-hover:w-2 h-px bg-brand-primary mr-0 group-hover:mr-2 transition-all duration-300"></span>
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section - Clean and Modern */}
        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-gray-300 text-sm font-medium text-left flex flex-col gap-6 w-full">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-12 lg:gap-16 w-full">
                {/* India Address Block */}
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <img src="https://flagcdn.com/w20/in.png" alt="India Flag" width="20" className="rounded-sm shadow-sm mt-1" />
                    <div>
                      <strong className="text-white block mb-1">INDIA</strong>
                      <span className="text-sm leading-relaxed opacity-80 block">
                        405, A wing, Samartha Aishwarya, Lokhandwala, Andheri (West), Mumbai - 400053.
                      </span>
                    </div>
                  </div>
                  <a href="tel:918454944090" className="text-gray-300 hover:text-brand-primary transition-colors text-sm flex items-center gap-2 pl-[32px] justify-start">
                    <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-white shrink-0">IN</span>
                    +91 84549 44090
                  </a>
                </div>

                {/* UAE Address Block */}
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <img src="https://flagcdn.com/w20/ae.png" alt="UAE Flag" width="20" className="rounded-sm shadow-sm mt-1" />
                    <div>
                      <strong className="text-white block mb-1">UAE</strong>
                      <span className="text-sm leading-relaxed opacity-80 block">
                        306, White Crown Building, Trade Center 1, Sheikh Zayed Rd, Dubai.
                      </span>
                    </div>
                  </div>
                  <a href="tel:+971501216802" className="text-gray-300 hover:text-brand-primary transition-colors text-sm flex items-center gap-2 pl-[32px] justify-start">
                    <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-white shrink-0">AE</span>
                    +971 50 121 6802
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Copyright & Powered By */}
          <div className="mt-8 pt-8 border-t border-white/5 flex flex-col sm:flex-row justify-between items-center gap-6">
            <div className="flex flex-col items-center sm:items-start gap-1">
              <span className="text-gray-300 text-sm font-medium">© {currentYear} L.I.S.T Learning Pathways</span>
              <div className="flex items-center gap-3 text-gray-500 text-sm text-center sm:text-left">
                <span>All rights reserved.</span>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white/5 px-4 py-2 rounded-full border border-white/10">
              <span className="text-gray-300 text-[10px] font-bold uppercase tracking-wider opacity-60">Powered by</span>
              <Link href="https://brandtalk.co.in/" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                <Image
                  src="/assets/img/bt-logo.webp"
                  width={50}
                  height={20}
                  alt="BT Logo"
                  className="object-contain opacity-50 hover:opacity-100 transition-opacity"
                />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Floating WhatsApp Icon */}
      <Link
        href={`https://wa.me/918454944090`}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-8 right-8 bg-gradient-to-br from-[#25D366] to-[#128C7E] text-white w-14 h-14 rounded-lg flex items-center justify-center shadow-[0_20px_40px_-15px_rgba(37,211,102,0.4)] hover:shadow-[0_25px_50px_-12px_rgba(37,211,102,0.5)] hover:-translate-y-1 active:scale-95 transition-all duration-300 z-50 group"
        aria-label="Chat on WhatsApp"
      >
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
        <span className="absolute right-full mr-4 bg-white text-brand-dark px-4 py-2 rounded-md text-sm font-bold shadow-2xl opacity-0 group-hover:opacity-100 translate-x-4 group-hover:translate-x-0 transition-all duration-300 pointer-events-none whitespace-nowrap">
          Chat with us
        </span>
      </Link>
    </footer>
  );
}
